import { Service, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, fromEvent, takeUntil } from 'rxjs';
import type { IListado, IQueryParam } from 'modelos';

import { queryParams } from '@core/datos/query-params';
import { SIN_NOTIFICAR_ERROR, contexto } from '@core/http/contexto';

/**
 * El único GET del buscador global.
 *
 * **Por qué no usa los `*.http.ts` de cada entidad.** Dos razones:
 *
 * - Los botones BLE y los usuarios tienen su servicio en `dominio/`, y
 *   `compartido/` no puede importar de ahí (regla de capas).
 * - Ninguno de esos `listar()` acepta un `AbortSignal`. El buscador sí lo
 *   necesita: con cada tecla sale una tanda de consultas contra colecciones de
 *   decenas de miles de documentos, y la tanda anterior se **corta**, no sólo se
 *   ignora al volver.
 *
 * Las rutas son las mismas que usan esos servicios (ADR-008: no se tocan).
 */
@Service()
export class BusquedaHttp {
  private readonly http = inject(HttpClient);

  /**
   * `silencioso`: si falla, no se avisa al usuario. Para quien ya muestra el
   * fallo en pantalla (el buscador global marca el grupo que no contestó): con
   * el aviso, además, salía uno por tecla y por colección.
   */
  listar<T>(
    ruta: string,
    query: IQueryParam,
    señal?: AbortSignal,
    opciones?: { silencioso?: boolean },
  ): Promise<IListado<T>> {
    const vacio = { datos: [] } as IListado<T>;
    if (señal?.aborted) return Promise.resolve(vacio);
    let pedido$ = this.http.get<IListado<T>>(ruta, {
      params: queryParams(query),
      context: opciones?.silencioso ? contexto(SIN_NOTIFICAR_ERROR) : undefined,
    });
    // Desuscribirse cancela el pedido HTTP en curso.
    if (señal) pedido$ = pedido$.pipe(takeUntil(fromEvent(señal, 'abort')));
    return firstValueFrom(pedido$, { defaultValue: vacio });
  }
}
